/**
 * Teinte de la pastille d'un repas — pur, sans React.
 *
 * Le prototype (`design/nutri-redesign.html`) donne une couleur et un glyphe à
 * chacun de ses cinq repas : éclair vert pour le pré-entraînement, soleil orange
 * pour le petit-déjeuner, couvert corail pour le déjeuner, pomme rose pour la
 * collation, lune indigo pour le dîner. Il les écrit en dur, parce qu'une
 * maquette connaît ses cinq repas. L'application, elle, ne les connaît pas : un
 * repas est une ligne créée depuis un modèle de journée ou ajoutée à la main,
 * avec le nom qu'on veut.
 *
 * 🔴 **Ces teintes sont DÉCORATIVES, et elles ne portent aucun sens.** Elles ne
 * disent ni un état, ni une macro, ni une catégorie que l'application
 * connaîtrait — le nom du repas est juste au-dessus, en toutes lettres, et c'est
 * lui qui l'identifie. Deux conséquences à ne pas perdre de vue :
 *
 *   1. **se tromper de teinte est sans conséquence** — d'où le repli par
 *      position plus bas, qui n'a pas besoin d'être « juste » ;
 *   2. elles sont **distinctes des couleurs de macros** même quand la valeur
 *      coïncide (`--m-breakfast` vaut le même orange que `--carbs`). Deux rôles,
 *      deux jetons : une pastille de repas qui changerait de teinte parce qu'on
 *      a retouché les glucides serait un défaut, pas une conséquence.
 *
 * ⚠️ **Ici et pas dans un module de composants** : `react-refresh` interdit à un
 * module exportant des composants d'exporter autre chose. Même contrainte que
 * `components/ui/tokens.ts` et `components/nourri/ratio.ts`.
 */

/** Les cinq teintes du prototype, dans son ordre. */
export type MealTone = 'preworkout' | 'breakfast' | 'lunch' | 'snack' | 'dinner';

const ORDRE: readonly MealTone[] = ['preworkout', 'breakfast', 'lunch', 'snack', 'dinner'];

/**
 * Reconnaissance par le nom.
 *
 * ⚠️ **L'ordre des règles compte, et c'est tout ce qui les rend justes.**
 * « Petit-déjeuner » contient « déjeuner » : tester le déjeuner d'abord
 * peindrait le petit-déjeuner en corail. Le pré-entraînement passe avant tout
 * le reste pour la même raison — « Collation pré-entraînement » est d'abord un
 * pré-entraînement.
 *
 * ⚠️ Comparaison sur un nom **replié** (minuscules, accents retirés) : « Dîner »,
 * « diner » et « DÎNER » sont le même repas. C'est le même repli que la
 * recherche d'aliments côté serveur, pour la même raison — le `LIKE` de SQLite
 * ne sait pas le faire, et une comparaison naïve ne le sait pas non plus.
 */
const REGLES: readonly { tone: MealTone; motif: RegExp }[] = [
  { tone: 'preworkout', motif: /\bpre|avant|intra|post|entrain|muscu|seance|sport/ },
  { tone: 'breakfast', motif: /petit|matin|reveil|breakfast|brunch/ },
  { tone: 'dinner', motif: /diner|soir|souper|dinner/ },
  { tone: 'lunch', motif: /dejeuner|midi|lunch|repas/ },
  { tone: 'snack', motif: /collation|gouter|encas|en-cas|snack|grignot/ },
];

/** Minuscules, accents retirés — « Dîner » et « diner » doivent coïncider. */
function fold(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

/**
 * @param name nom du repas, tel qu'il s'affiche
 * @param index position dans la journée — sert de repli
 *
 * ⚠️ **Le repli est la position, pas un hachage du nom.** Un hachage donnerait
 * une teinte stable d'un jour à l'autre, ce qui est tentant, mais il peut
 * donner la **même** teinte à deux repas voisins — et c'est précisément ce que
 * la pastille sert à éviter. La position, elle, garantit que cinq repas
 * consécutifs sont toujours de cinq couleurs différentes, ce qui est le seul
 * service qu'on lui demande.
 */
export function mealTone(name: string, index: number): MealTone {
  const replie = fold(name);
  for (const regle of REGLES) {
    if (regle.motif.test(replie)) return regle.tone;
  }
  return ORDRE[index % ORDRE.length] ?? 'lunch';
}
